import React from 'react';
import {Badge} from 'reactstrap';
import {Tag, LucideIcon} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {Tag as TagType} from '../../../types';

interface DisplayTagProps {
    tag: TagType | Partial<TagType>;
    size?: 'sm' | 'md' | 'lg';
    showIcon?: boolean;
    minimal?: boolean;
    className?: string;
    onClick?: () => void;
    onRemove?: () => void;
}

const DisplayTag: React.FC<DisplayTagProps> = ({
    tag,
    size = 'md',
    showIcon = true,
    minimal = false,
    className = '',
    onClick,
    onRemove
}) => {
    // Get the icon component from lucide-react
    const getIconComponent = (iconName?: string): LucideIcon => {
        if (!iconName) return Tag;

        // Convert icon name to PascalCase if needed
        const pascalCaseIconName =
            iconName.charAt(0).toUpperCase() + iconName.slice(1);

        // Try to get the icon from lucide-react
        const iconsByName = LucideIcons as unknown as Record<
            string,
            LucideIcon | undefined
        >;
        const IconComponent =
            iconsByName[pascalCaseIconName] || iconsByName[iconName] || Tag;

        return IconComponent;
    };

    const IconComponent = getIconComponent(tag.icon);

    const sizeClasses = {
        sm: 'px-2 py-1 small',
        md: 'px-2 py-1',
        lg: 'px-3 py-2'
    };

    const iconSizes = {
        sm: 12,
        md: 14,
        lg: 16
    };

    const badgeStyle = {
        backgroundColor: tag.colour || '#6c757d',
        color: getContrastColor(tag.colour || '#6c757d'),
        border: 'none',
        cursor: onClick ? 'pointer' : 'default'
    };

    // Minimal mode: circular badge with icon only
    if (minimal) {
        const minimalSizes = {
            sm: 20,
            md: 24,
            lg: 28
        };

        const minimalIconSizes = {
            sm: 12,
            md: 14,
            lg: 16
        };

        return (
            <div
                className={`d-inline-flex align-items-center justify-content-center ${className}`}
                style={{
                    width: `${minimalSizes[size]}px`,
                    height: `${minimalSizes[size]}px`,
                    borderRadius: '50%',
                    backgroundColor: tag.colour || '#6c757d',
                    cursor: onClick ? 'pointer' : 'default',
                    flexShrink: 0
                }}
                onClick={onClick}
                title={tag.name}
            >
                <IconComponent
                    size={minimalIconSizes[size]}
                    style={{color: getContrastColor(tag.colour || '#6c757d')}}
                />
            </div>
        );
    }

    // Standard badge mode
    return (
        <Badge
            key={tag.id || `temp-${tag.name}`}
            className={`d-inline-flex align-items-center gap-1 ${sizeClasses[size]} ${className}`}
            style={badgeStyle}
            onClick={onClick}
            color=""
        >
            {showIcon && <IconComponent size={iconSizes[size]} />}
            <span>{tag.name}</span>
            {onRemove && (
                <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{fontSize: '0.6em'}}
                    onClick={e => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    aria-label="Remove tag"
                />
            )}
        </Badge>
    );
};

// Helper function to determine if text should be light or dark based on background color
function getContrastColor(hexColor: string): string {
    // Remove # if present
    const hex = hexColor.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return black for light backgrounds, white for dark backgrounds
    return luminance > 0.5 ? '#000000' : '#ffffff';
}

export default DisplayTag;
